import { useMemo, useState } from 'react'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaChevronDown, FaDownload, FaExternalLinkAlt, FaFolderOpen, FaPaperPlane, FaPencilAlt, FaUser } from 'react-icons/fa'
import api from '../../../../../helpers/apiRequester'
import { Button, Card, Column, DataTable, EmptyState, KeyValueList, Modal, StatusPill, cx } from '../../../../../ui'
import { ClientDocument, ClientDocumentGap, formatBytes, formatDateTime } from '../shared'
import s from './styles.module.scss'

export type DocumentsStatus = 'loading' | 'ready' | 'error'

export interface DocumentsClaim {
    case_ref: string
    lender: string | null
    base_claim: boolean
    cancelled: boolean
}

export interface DocumentsState {
    status: DocumentsStatus
    documents: ClientDocument[]
    missing: ClientDocumentGap[]
    /** Every claim the backend checked a folder for, including ones with no files yet. */
    claims: DocumentsClaim[]
    checkedLocations: number
    error?: string
}

export interface DocumentsCardProps {
    origin: string
    canSendLoc: boolean
    state: DocumentsState
    onRetry: () => void
}

interface DocumentGroup {
    id: string
    scope: 'client' | 'claim'
    claim?: DocumentsClaim
    /** Final documents: signed copies, certificates, reports. */
    documents: ClientDocument[]
    /** Pre-signature drafts, shown in their own sub-section. */
    drafts: ClientDocument[]
    missing: ClientDocumentGap[]
}

const fileKind = (contentType: string) => {
    if (contentType === 'application/pdf') return 'PDF'
    if (contentType.startsWith('image/')) return 'Image'
    if (contentType === 'application/json') return 'JSON'
    if (contentType === 'text/csv') return 'CSV'
    return 'File'
}

const isSignedCopy = (d: ClientDocument) => /-signed\.[a-z0-9]+$/i.test(d.file_name)

const documentStatus = (d: ClientDocument) => {
    if (d.draft)
        return (
            <StatusPill size="sm" tone="warn" dot>
                Unsigned draft
            </StatusPill>
        )
    if (isSignedCopy(d)) {
        // The base pack holds the original the client signed; claim folders get a copy of it.
        const original = d.registry_key?.startsWith('base')
        return (
            <StatusPill
                size="sm"
                tone="good"
                dot
                title={original ? 'The original in the signed base-claim pack' : 'Copy filed against this claim'}
            >
                {original ? 'Signed original' : 'Signed copy'}
            </StatusPill>
        )
    }
    if (!d.registered)
        return (
            <StatusPill size="sm" title="Found in the document folder but not named in the registry">
                Supporting file
            </StatusPill>
        )
    return <StatusPill size="sm">Final</StatusPill>
}

/** Rows stay lean — name, status, type, size, date, View. The storage path and the rest live in the detail dialog. */
const columns: Column<ClientDocument>[] = [
    { key: 'label', header: 'Document', render: (d) => <span className={s.docName}>{d.label}</span> },
    { key: 'status', header: 'Status', nowrap: true, render: documentStatus },
    {
        key: 'kind',
        header: 'Type',
        nowrap: true,
        render: (d) => <StatusPill size="sm">{fileKind(d.content_type)}</StatusPill>,
    },
    { key: 'size', header: 'Size', align: 'right', nowrap: true, render: (d) => formatBytes(d.size) },
    { key: 'last_modified', header: 'Updated', nowrap: true, render: (d) => formatDateTime(d.last_modified) },
    {
        key: 'actions',
        header: '',
        align: 'right',
        nowrap: true,
        render: (d) =>
            d.url ? (
                <Button
                    as="a"
                    size="sm"
                    variant="ghost"
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<FaExternalLinkAlt />}
                    onClick={(e) => e.stopPropagation()}
                >
                    View
                </Button>
            ) : null,
    },
]

/**
 * Splits the flat document list into one group for the client record
 * (credit reports, AML — anything not tied to a claim) followed by one group
 * per claim, base claims first. Each group separates final documents from
 * unsigned drafts. Claims with nothing on file still get a group so the gap
 * is visible rather than silently absent.
 */
const buildGroups = (
    documents: ClientDocument[],
    missing: ClientDocumentGap[],
    claims: DocumentsClaim[],
): DocumentGroup[] => {
    const split = (docs: ClientDocument[]) => ({
        documents: docs.filter((d) => !d.draft),
        drafts: docs.filter((d) => d.draft),
    })

    const client: DocumentGroup = {
        id: 'client',
        scope: 'client',
        ...split(documents.filter((d) => d.scope === 'client')),
        missing: missing.filter((g) => !g.case_ref),
    }

    const byRef = new Map<string, DocumentsClaim>()
    for (const c of claims) if (c.case_ref) byRef.set(c.case_ref, c)
    // A document can reference a claim the leads query did not return; keep it visible.
    for (const d of documents) {
        if (d.scope === 'claim' && d.case_ref && !byRef.has(d.case_ref)) {
            byRef.set(d.case_ref, {
                case_ref: d.case_ref,
                lender: d.lender ?? null,
                base_claim: Boolean(d.base_claim),
                cancelled: Boolean(d.cancelled),
            })
        }
    }

    const claimGroups: DocumentGroup[] = Array.from(byRef.values())
        .sort((a, b) => {
            if (a.base_claim !== b.base_claim) return a.base_claim ? -1 : 1
            if (a.cancelled !== b.cancelled) return a.cancelled ? 1 : -1
            return a.case_ref.localeCompare(b.case_ref)
        })
        .map((claim) => ({
            id: claim.case_ref,
            scope: 'claim',
            claim,
            ...split(documents.filter((d) => d.scope === 'claim' && d.case_ref === claim.case_ref)),
            missing: missing.filter((g) => g.case_ref === claim.case_ref),
        }))

    return [client, ...claimGroups]
}

interface GroupHeaderProps {
    group: DocumentGroup
    open: boolean
    onToggle: () => void
    canSendLoc: boolean
    sending: boolean
    onSendLoc: (claim: DocumentsClaim) => void
}

/** Group heading doubling as the collapse toggle, so a folder can be tucked away once it has been checked. */
const GroupHeader = ({ group, open, onToggle, canSendLoc, sending, onSendLoc }: GroupHeaderProps) => {
    const count = group.documents.length + group.drafts.length
    const trailing = (
        <>
            <StatusPill round tone={count ? 'accent' : 'neutral'} title={`${count} file${count === 1 ? '' : 's'}`}>
                {count}
            </StatusPill>
            <span className={cx(s.docGroupChevron, open && s.docGroupChevronOpen)} aria-hidden="true">
                <FaChevronDown />
            </span>
        </>
    )

    if (group.scope === 'client') {
        return (
            <button
                type="button"
                className={s.docGroupHeader}
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={`docs-${group.id}`}
            >
                <span className={s.docGroupIcon} aria-hidden="true">
                    <FaUser />
                </span>
                <span className={s.docGroupText}>
                    <span className={s.docGroupTitle}>Client documents</span>
                    <span className={s.docGroupMeta}>Held against the client rather than any one claim.</span>
                </span>
                {trailing}
            </button>
        )
    }

    const claim = group.claim!
    const sendDisabled = sending || !claim.lender
    return (
        <div className={s.docGroupBar}>
            <button
                type="button"
                className={s.docGroupHeader}
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={`docs-${group.id}`}
            >
                <span className={s.docGroupIcon} aria-hidden="true">
                    <FaFolderOpen />
                </span>
                <span className={s.docGroupText}>
                    <span className={s.docGroupTitle}>
                        <span className={s.mono}>{claim.case_ref}</span>
                        {claim.base_claim && (
                            <StatusPill
                                size="sm"
                                tone="accent"
                                title="The signed pack (CFA, LOA, LOC…) lives on this claim"
                            >
                                Base claim
                            </StatusPill>
                        )}
                        {claim.cancelled && (
                            <StatusPill size="sm" tone="bad">
                                Cancelled
                            </StatusPill>
                        )}
                    </span>
                    <span className={s.docGroupMeta}>{claim.lender || 'Lender not yet identified'}</span>
                </span>
            </button>
            {canSendLoc && (
                <div className={s.docGroupActions}>
                    <Button
                        size="sm"
                        variant="ghost"
                        icon={<FaPaperPlane />}
                        loading={sending}
                        disabled={sendDisabled}
                        title={claim.lender ? 'Email the LOC pack to the lender' : 'This claim has no lender name'}
                        onClick={() => onSendLoc(claim)}
                    >
                        Send to Lender
                    </Button>
                </div>
            )}
            <button
                type="button"
                className={s.docGroupEnd}
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={`docs-${group.id}`}
                aria-label={open ? 'Collapse claim documents' : 'Expand claim documents'}
            >
                {trailing}
            </button>
        </div>
    )
}

const GroupGaps = ({ group }: { group: DocumentGroup }) =>
    group.missing.length ? (
        <div className={s.docGroupGaps}>
            <span className={s.docGapsLabel}>Not on file</span>
            {group.missing.map((gap) => (
                <StatusPill key={gap.registry_key} size="sm" tone="warn">
                    {gap.label}
                </StatusPill>
            ))}
        </div>
    ) : null

const appliesTo = (d: ClientDocument) =>
    d.scope === 'client' ? (
        'Client record — not tied to a single claim'
    ) : (
        <span className={s.docLabel}>
            <span className={s.mono}>{d.case_ref}</span>
            {d.lender && <span>· {d.lender}</span>}
            {d.base_claim && (
                <StatusPill size="sm" tone="accent">
                    Base claim
                </StatusPill>
            )}
            {d.cancelled && (
                <StatusPill size="sm" tone="bad">
                    Cancelled
                </StatusPill>
            )}
        </span>
    )

/** Full detail for one file, opened by clicking its row. */
const DocumentDetail = ({ document: d, onClose }: { document: ClientDocument | null; onClose: () => void }) => (
    <Modal
        open={Boolean(d)}
        onClose={onClose}
        size="md"
        title={d?.label}
        subtitle={d?.file_name}
        footer={
            d && (
                <>
                    <Button variant="neutral" onClick={onClose}>
                        Close
                    </Button>
                    {d.download_url && (
                        <Button as="a" variant="neutral" href={d.download_url} icon={<FaDownload />}>
                            Download
                        </Button>
                    )}
                    {d.url && (
                        <Button
                            as="a"
                            href={d.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            icon={<FaExternalLinkAlt />}
                        >
                            View document
                        </Button>
                    )}
                </>
            )
        }
    >
        {d && (
            <KeyValueList
                items={[
                    { label: 'Status', value: documentStatus(d) },
                    { label: 'Applies to', value: appliesTo(d) },
                    { label: 'Type', value: `${fileKind(d.content_type)} · ${d.content_type}` },
                    { label: 'Size', value: formatBytes(d.size) },
                    { label: 'Last updated', value: formatDateTime(d.last_modified) },
                    {
                        label: 'Registry entry',
                        value: d.registered ? (
                            <span className={s.mono}>{d.registry_key}</span>
                        ) : (
                            <span className={s.muted}>Not named in the introducer registry</span>
                        ),
                    },
                    { label: 'File name', value: d.file_name, mono: true },
                    { label: 'Storage key', value: d.key, mono: true },
                ]}
            />
        )}
    </Modal>
)

/**
 * Every document on file in S3 for the client, resolved from the introducer
 * registry's document paths. Client-level files (credit report, AML) come
 * first; everything else is filed under the claim it belongs to, with the
 * pre-signature drafts in their own sub-section. Click a row for the detail.
 */
export const DocumentsCard = ({ origin, canSendLoc, state, onRetry }: DocumentsCardProps) => {
    const { status, documents, missing, claims, checkedLocations } = state
    const groups = useMemo(() => buildGroups(documents, missing, claims), [documents, missing, claims])
    const [selected, setSelected] = useState<ClientDocument | null>(null)
    const [expanded, setExpanded] = useState<string[]>(['client'])
    const [sendingCaseRef, setSendingCaseRef] = useState<string | null>(null)
    const toggleGroup = (id: string) => setExpanded((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]))

    const sendLoc = async (claim: DocumentsClaim) => {
        setSendingCaseRef(claim.case_ref)
        try {
            const { data } = await api<{
                success: boolean
                message?: string
                toEmail?: string
                lenderName?: string
            }>({
                url: 'comms/loc-batches/send-one',
                data: { origin, caseRef: claim.case_ref },
            })
            if (!data?.success) {
                Report.failure('LOC not sent', data?.message || 'The LOC pack could not be sent.', 'OK')
                return
            }
            Report.success(
                'LOC sent',
                `The LOC pack for ${claim.case_ref} was sent to ${data.toEmail || 'the lender on file'} (${data.lenderName || claim.lender}). It will show on LOC batches.`,
                'OK',
            )
        } catch (error: any) {
            const statusCode = error?.response?.status
            const message = error?.response?.data?.message || error?.message || 'The LOC pack could not be sent.'
            if (statusCode === 403) {
                Report.failure('Access Denied', 'Sending an LOC pack is restricted to the same users as LOC batches.', 'OK')
                return
            }
            Report.failure('LOC not sent', message, 'OK')
        } finally {
            setSendingCaseRef(null)
        }
    }

    const confirmSendLoc = (claim: DocumentsClaim) => {
        Confirm.show(
            `Send LOC and LOA?`,
            `This sends a single email containing the LOC and LOA to the lender on file for ${claim.lender}.`,
            'Send to lender',
            'Go back',
            () => void sendLoc(claim),
        )
    }

    const countPill =
        status === 'ready' ? (
            <StatusPill round tone={documents.length ? 'accent' : 'neutral'}>
                {documents.length}
            </StatusPill>
        ) : status === 'loading' ? (
            <StatusPill>Loading…</StatusPill>
        ) : (
            <StatusPill tone="bad" dot>
                Unavailable
            </StatusPill>
        )

    const claimCount = groups.length - 1
    const draftCount = documents.filter((d) => d.draft).length
    const footer =
        status === 'ready' ? (
            <div className={s.docFooter}>
                <span>
                    {missing.length
                        ? `${missing.length} expected document${missing.length === 1 ? '' : 's'} not on file.`
                        : 'All expected base-claim documents are on file.'}
                    {draftCount ? ` ${draftCount} unsigned draft${draftCount === 1 ? '' : 's'}.` : ''}
                </span>
                <span className={s.docChecked}>
                    {claimCount} claim folder{claimCount === 1 ? '' : 's'} · checked {checkedLocations} storage location
                    {checkedLocations === 1 ? '' : 's'}
                </span>
            </div>
        ) : undefined

    let body
    if (status === 'error') {
        body = (
            <div className={s.docInset}>
                <EmptyState
                    tone="error"
                    title="Could not list documents"
                    description={state.error || 'The document service did not respond.'}
                    action={
                        <Button size="sm" onClick={onRetry}>
                            Retry
                        </Button>
                    }
                />
            </div>
        )
    } else if (status === 'loading') {
        body = <DataTable bare dense loading columns={columns} rows={[]} rowKey={(d) => d.key} />
    } else if (!documents.length && claimCount === 0) {
        body = (
            <div className={s.docInset}>
                <EmptyState
                    title="No documents on file"
                    description="Nothing has been generated or uploaded to S3 for this client yet."
                />
            </div>
        )
    } else {
        body = (
            <div className={s.docGroups}>
                {groups.map((group) => (
                    <section key={group.id} className={s.docGroup} aria-label={group.id}>
                        <GroupHeader
                            group={group}
                            open={expanded.includes(group.id)}
                            onToggle={() => toggleGroup(group.id)}
                            canSendLoc={canSendLoc && group.scope === 'claim'}
                            sending={Boolean(group.claim && sendingCaseRef === group.claim.case_ref)}
                            onSendLoc={confirmSendLoc}
                        />
                        <div id={`docs-${group.id}`} hidden={!expanded.includes(group.id)}>
                            {group.documents.length ? (
                                <DataTable
                                    bare
                                    dense
                                    columns={columns}
                                    rows={group.documents}
                                    rowKey={(d) => d.key}
                                    onRowClick={setSelected}
                                />
                            ) : (
                                <p className={s.docGroupEmpty}>
                                    {group.scope === 'client'
                                        ? 'No client-level documents — run a credit report to add one.'
                                        : group.drafts.length
                                          ? 'No signed documents yet — only drafts.'
                                          : 'Nothing on file for this claim yet.'}
                                </p>
                            )}
                            {group.drafts.length > 0 && (
                                <>
                                    <div className={s.docSubHeader}>
                                        <FaPencilAlt aria-hidden="true" />
                                        <span>Unsigned drafts</span>
                                        <span className={s.docSubHeaderMeta}>
                                            Pre-signature copies generated before the client signed
                                        </span>
                                    </div>
                                    <DataTable
                                        bare
                                        dense
                                        columns={columns}
                                        rows={group.drafts}
                                        rowKey={(d) => d.key}
                                        rowTone={() => 'warn'}
                                        onRowClick={setSelected}
                                    />
                                </>
                            )}
                            <GroupGaps group={group} />
                        </div>
                    </section>
                ))}
            </div>
        )
    }

    return (
        <>
            <Card
                title="Documents"
                subtitle={`Everything on file in S3 for this client, resolved from the ${origin} document registry. Click a row for details.`}
                actions={countPill}
                padding="none"
                footer={footer}
            >
                {body}
            </Card>
            <DocumentDetail document={selected} onClose={() => setSelected(null)} />
        </>
    )
}
